/**
 * Sektoren, Reihen und Feld-IDs.
 *
 * Das Brett hat sechs Außenkanten und damit sechs Sektoren. Die 216 Felder außerhalb
 * des Zentrums sind **trennscharf** auf die Sektoren verteilt — 6 × 36, kein Feld
 * gehört zwei Sektoren (`docs/GAME_RULES.md`, Abschnitt 2; vormals
 * `OPEN_QUESTIONS.md` Nr. 7).
 *
 * Aufbau, zurückgerechnet aus `docs/board-reference.svg` und dort verifiziert:
 *
 * - Der **Reihenbuchstabe ist die Hex-Distanz**: A = Radius (Außenreihe) … H = 1.
 * - Jede Reihe mit Distanz `d` besteht aus sechs geraden Segmenten mit je `d` Feldern.
 * - Segment `k` beginnt am Eckfeld `d * HEX_DIRECTIONS[k]` und läuft in Richtung
 *   `HEX_DIRECTIONS[(k + 2) % 6]`. Das ist der kanonische Hex-Ring-Rundgang, dem die
 *   Referenzgrafik folgt.
 * - Die Feldnummer zählt innerhalb des Segments ab 1, beginnend am Eckfeld.
 *
 * **Sektor-Identifier sind Geometrie-Labels, keine Spielerfarben.** `green` benennt
 * den Sektor, der in der Referenzgrafik grün gezeichnet ist. Welcher Spieler welchen
 * Sektor erhält, entscheidet der Host bei der Partieerstellung (Phase 8) — diese
 * Benennung sagt darüber nichts aus.
 */
import { boardCells } from './board.js';
import { HEX_DIRECTIONS, hex, hexDistance, hexKey, hexRing } from './hex.js';
/**
 * Alle Sektoren im Uhrzeigersinn der Darstellung, wie in den Metadaten von
 * `docs/board-reference.svg` (`sector_order_clockwise`).
 */
export const SECTOR_IDS = ['green', 'yellow', 'red', 'blue', 'orange', 'purple'];
/**
 * Welcher Sektor an welchem Eckfeld beginnt: Index `k` meint die Ecke
 * `radius * HEX_DIRECTIONS[k]`. Aus der Referenzgrafik abgeleitet und durch
 * `reference-graphic.test.ts` für alle 216 Felder abgesichert.
 */
const SECTOR_BY_CORNER = ['yellow', 'green', 'purple', 'orange', 'blue', 'red'];
/**
 * Die sechs Sektoren **in ihrer Reihenfolge um das Brett**.
 *
 * Dieselbe Liste wie `SECTOR_BY_CORNER`, und das ist kein Zufall: aufeinanderfolgende Ecken sind
 * benachbart, also auch die Sektoren, die an ihnen beginnen. `SECTOR_IDS` ist dagegen nur eine
 * Aufzählung — ihre Reihenfolge sagt **nichts** über die Lage, und wer sie dafür benutzt, bekommt
 * ein Ergebnis, das in der Hälfte der Fälle zufällig stimmt.
 *
 * Gebraucht wird sie, seit die Partieerstellung Sektoren vorschlägt (Zyklus 10e): gegenüber bei
 * 1:1 (§8.1 nennt das den Normalfall), gleichmäßig verteilt bei drei, vier und sechs Spielern
 * (`GAME_RULES.md` Abschnitt 12). Sie gehört hierher und nicht in die Oberfläche, weil sie
 * **logische Boardstruktur** ist — abgeschrieben wäre sie eine zweite Wahrheit über die Geometrie
 * (`CLAUDE.md` Regel 9). Geprüft wird sie gegen die Felder: benachbarte Einträge berühren sich,
 * gegenüberliegende nicht.
 *
 * **Der Host bleibt frei** (Nr. 8): dies ist Geometrie, kein Muster, das jemand einhalten müsste.
 */
export const SECTOR_RING = SECTOR_BY_CORNER;
/**
 * Der Sektor, der diesem gegenüberliegt.
 *
 * Bei sechs Sektoren um ein Zentrum ist das der Eintrag drei Plätze weiter — und die einzige
 * Paarung, die sich **nicht** berührt.
 */
export function oppositeSector(sector) {
    const index = SECTOR_RING.indexOf(sector);
    if (index < 0) {
        throw new Error(`Kein Sektor: ${sector}`);
    }
    return SECTOR_RING[(index + SECTOR_RING.length / 2) % SECTOR_RING.length];
}
export const GOAL_CELL_ID = 'goal-cell';
const LETTER_A = 'A'.charCodeAt(0);
const ALPHABET_LENGTH = 26;
/** Reihenbuchstabe für eine Distanz: Außenreihe ist A, das innerste Feld der höchste Buchstabe. */
function rowLetter(radius, distance) {
    const offset = radius - distance;
    if (offset < 0 || offset >= ALPHABET_LENGTH) {
        throw new RangeError(`Reihe für Distanz ${distance} bei Radius ${radius} lässt sich nicht als Buchstabe benennen`);
    }
    return String.fromCharCode(LETTER_A + offset);
}
export function createSectorLayout(board) {
    const naming = new Map();
    const byId = new Map();
    const bySector = new Map(SECTOR_IDS.map((sector) => [sector, []]));
    byId.set(GOAL_CELL_ID, hex(0, 0));
    for (let distance = 1; distance <= board.radius; distance++) {
        const row = rowLetter(board.radius, distance);
        const ring = hexRing(distance);
        // hexRing beginnt an der Ecke `distance * HEX_DIRECTIONS[4]` und läuft in
        // Richtung HEX_DIRECTIONS[0]. Segment `s` startet damit an der Ecke
        // `distance * HEX_DIRECTIONS[(s + 4) % 6]`.
        for (let segment = 0; segment < HEX_DIRECTIONS.length; segment++) {
            const corner = (segment + 4) % HEX_DIRECTIONS.length;
            const sector = SECTOR_BY_CORNER[corner];
            for (let step = 0; step < distance; step++) {
                const cell = ring[segment * distance + step];
                const index = step + 1;
                naming.set(hexKey(cell), { sector, row, index });
                byId.set(`${sector}-${row}${index}`, cell);
                bySector.get(sector).push(cell);
            }
        }
    }
    return { board, naming, byId, bySector };
}
export function cellSector(layout, cell) {
    return layout.naming.get(hexKey(cell))?.sector;
}
export function cellRow(layout, cell) {
    return layout.naming.get(hexKey(cell))?.row;
}
export function cellRowIndex(layout, cell) {
    return layout.naming.get(hexKey(cell))?.index;
}
/**
 * Die kanonische Feld-ID, identisch mit den IDs der Referenzgrafik: `green-A1`.
 * Das Zentrum heißt `goal-cell` und hat keinen Sektor.
 */
export function cellId(layout, cell) {
    const naming = layout.naming.get(hexKey(cell));
    if (naming === undefined) {
        if (hexDistance(hex(0, 0), cell) === 0)
            return GOAL_CELL_ID;
        throw new RangeError(`Feld (${cell.q}, ${cell.r}) liegt nicht auf dem Brett`);
    }
    return `${naming.sector}-${naming.row}${naming.index}`;
}
/** Umkehrung von `cellId`. Unbekannte IDs ergeben `undefined`, sie werfen nicht. */
export function cellOfId(layout, id) {
    return layout.byId.get(id);
}
export function sectorCells(layout, sector) {
    return layout.bySector.get(sector) ?? [];
}
/** Alle Felder des Bretts mit ihrer kanonischen ID — für Debug-Ausgaben und KI-Kontext. */
export function labelledCells(layout) {
    return boardCells(layout.board).map((cell) => ({ cell, id: cellId(layout, cell) }));
}
//# sourceMappingURL=sectors.js.map